"use client";

/*
 * The organisations showcase, its Home logo row, and the community figures.
 * Everything here renders only what src/content/organisations.ts marks as
 * approved or filled in, so an empty list shows nothing rather than a gap.
 * Logos are shown as supplied (never recoloured or cropped), at one height,
 * and every card opens the organisation's own site in a new tab.
 */

import { Box, Card, Container, Typography } from "@mui/material";
import Image from "next/image";
import { TULUS } from "../brand";
import {
  COMMUNITY,
  communityReady,
  ORGANISATIONS_COPY,
  shownOrganisations,
  type OrgGroup,
  type Organisation,
} from "../content/organisations";
import { SectionHeading, TextLink } from "./Sections";
import type { Language } from "./useLanguage";

const GROUP_ORDER: OrgGroup[] = ["zakat", "wakaf", "finance", "platform"];

function LogoCard({ org, lang }: { org: Organisation; lang: Language }) {
  const c = ORGANISATIONS_COPY[lang];
  return (
    <Card
      component="a"
      href={org.url}
      target="_blank"
      rel="noopener noreferrer"
      data-org={org.id}
      sx={{
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.25,
        textAlign: "center",
        textDecoration: "none",
        height: "100%",
        transition: "border-color .15s, box-shadow .15s",
        "&:hover, &:focus-visible": { borderColor: TULUS.cyan },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: org.logoOnDark ? 80 : 64,
          display: "grid",
          placeItems: "center",
          ...(org.logoOnDark ? { backgroundColor: TULUS.ink, borderRadius: "12px", "& img": { padding: "8px 12px" } } : {}),
        }}
      >
        {org.logo ? (
          <Image src={org.logo} alt="" fill sizes="180px" style={{ objectFit: "contain" }} />
        ) : (
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: TULUS.ink }}>{org.short}</Typography>
        )}
      </Box>
      <Typography sx={{ fontWeight: 500, fontSize: "0.88rem", color: TULUS.ink, lineHeight: 1.35 }}>
        {org.name}
        {/* Visually hidden, for screen readers. MUI reads width: 1 as 100%, so the 1px is a string. */}
        <Box component="span" sx={{ position: "absolute", width: "1px", height: "1px", overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
          {" "}({c.newTab})
        </Box>
      </Typography>
      {org.role && <Typography sx={{ fontSize: "0.82rem", color: TULUS.mutedText, lineHeight: 1.45 }}>{org.role[lang]}</Typography>}
    </Card>
  );
}

const grid = { display: "grid", gap: 2, gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" } };

export function CommunityFigures({ lang }: { lang: Language }) {
  if (!communityReady()) return null;
  const c = ORGANISATIONS_COPY[lang].community;
  const locale = lang === "ms" ? "ms-MY" : "en-MY";
  const counts = COMMUNITY.counts as Record<"masjid" | "surau" | "orphanages", number>;
  const total = counts.masjid + counts.surau + counts.orphanages;
  const date = new Date(COMMUNITY.asOf + "T00:00:00+08:00").toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Kuala_Lumpur" });
  return (
    <Box data-testid="community-figures" sx={{ mt: 6 }}>
      <Typography variant="h3" sx={{ fontSize: "1.2rem", color: TULUS.ink, mb: 1 }}>{c.title}</Typography>
      <Typography sx={{ color: TULUS.ink, fontSize: "1.05rem", mb: 2.5 }}>{c.lead(total.toLocaleString(locale))}</Typography>
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, maxWidth: 760 }}>
        {(["masjid", "surau", "orphanages"] as const).map((k) => (
          <Card key={k} sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: "2rem", fontWeight: 700, color: TULUS.cyanText, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
              {counts[k].toLocaleString(locale)}
            </Typography>
            <Typography sx={{ color: TULUS.mutedText }}>{c.labels[k]}</Typography>
          </Card>
        ))}
      </Box>
      <Typography sx={{ fontSize: "0.85rem", color: TULUS.mutedText, mt: 1.5 }}>{c.source(date)}</Typography>
    </Box>
  );
}

/** The full showcase on /kerjasama: approved organisations by group, then the community figures. */
export function OrganisationsShowcase({ lang }: { lang: Language }) {
  const shown = shownOrganisations();
  if (!shown.length && !communityReady()) return null;
  const c = ORGANISATIONS_COPY[lang];
  return (
    <Box component="section" id="organisasi" sx={{ py: { xs: 6, md: 9 }, scrollMarginTop: 16 }}>
      <Container maxWidth="lg">
        {shown.length > 0 && (
          <>
            <SectionHeading>{c.title}</SectionHeading>
            <Typography sx={{ color: TULUS.ink, fontSize: "1.05rem", lineHeight: 1.75, maxWidth: 720, mb: 1 }}>{c.intro}</Typography>
            {GROUP_ORDER.map((g) => {
              const list = shown.filter((o) => o.group === g);
              if (!list.length) return null;
              return (
                <Box key={g} sx={{ mt: 4 }}>
                  <Typography variant="h3" sx={{ fontSize: "1.1rem", color: TULUS.ink, mb: 2 }}>{c.groups[g]}</Typography>
                  <Box sx={grid}>
                    {list.map((o) => <LogoCard key={o.id} org={o} lang={lang} />)}
                  </Box>
                </Box>
              );
            })}
          </>
        )}
        <CommunityFigures lang={lang} />
      </Container>
    </Box>
  );
}

/** One row of approved zakat institutions on Home, linking to the full showcase. */
export function HomeOrganisationsRow({ lang }: { lang: Language }) {
  const zakat = shownOrganisations().filter((o) => o.group === "zakat");
  if (!zakat.length) return null;
  const c = ORGANISATIONS_COPY[lang];
  return (
    <Box component="section" aria-label={c.homeTitle} sx={{ py: { xs: 4, md: 5 }, borderBottom: `1px solid ${TULUS.line}` }}>
      <Container maxWidth="lg">
        <Typography sx={{ fontWeight: 500, color: TULUS.mutedText, mb: 2, textAlign: { xs: "left", md: "center" } }}>{c.homeTitle}</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 2, md: 3 }, justifyContent: { xs: "flex-start", md: "center" }, alignItems: "center" }}>
          {zakat.map((o) => (
            <Box
              key={o.id}
              component="a"
              href={o.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${o.name} (${c.newTab})`}
              title={o.name}
              data-org={o.id}
              sx={{
                position: "relative",
                width: 120,
                height: 56,
                display: "grid",
                placeItems: "center",
                textDecoration: "none",
                color: TULUS.ink,
                fontWeight: 700,
                ...(o.logoOnDark ? { backgroundColor: TULUS.ink, borderRadius: "10px", "& img": { padding: "6px 8px" } } : {}),
              }}
            >
              {o.logo ? <Image src={o.logo} alt="" fill sizes="120px" style={{ objectFit: "contain" }} /> : o.short}
            </Box>
          ))}
        </Box>
        <Box sx={{ mt: 2.5, textAlign: { xs: "left", md: "center" } }}>
          <TextLink action={{ label: c.homeLink, href: "/kerjasama#organisasi" }} />
        </Box>
      </Container>
    </Box>
  );
}
